/// <reference types="vite/client" />
/**
 * /templates — visual editor for the DB-backed email/LinkedIn templates
 * (Phase 21 #134). Master/detail: pick a template on the left, edit subject +
 * body + channel + active on the right with a live markdown preview, then Save
 * (PUT) or run a non-sending Preview & lint (POST). Reuses the operator token
 * shared with the Admin page (sessionStorage), and the token-gated
 * /api/_admin/templates endpoints shipped in #133.
 *
 * Guard rails live on the backend: variables are auto-derived from {{…}} tokens
 * and un-suppliable ones are refused (400), email cold-outbound must keep its
 * opt-out line, and the real anti-hype gate runs at SEND time — the lint shown
 * here is advisory only.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, Eye, Save, Plus, Trash2 } from "lucide-react";
import { renderMarkdown } from "../utils/render-markdown";

const SK_TOKEN = "levier.admin.token";

const CHANNELS = ["email", "linkedin_dm", "linkedin_post", "linkedin_comment"] as const;
type Channel = (typeof CHANNELS)[number];

interface Template {
  id: string;
  name: string;
  category: string;
  channel: string;
  subject: string | null;
  body_md: string;
  variables: string[];
  cta_label: string | null;
  cta_url: string | null;
  active: boolean;
}

interface PreviewResult {
  subject: string;
  html: string;
  text: string;
  variables: string[];
  lint: { verdict: "PASS" | "FAIL" | "BLOCK"; feedback: string[] };
  /** True when html is the full assembled email (render in an iframe). */
  full?: boolean;
}

const inputStyle = {
  background: "var(--color-bg-card)",
  color: "var(--color-text)",
  border: "1px solid var(--color-border)",
} as const;

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
// Mirrors the backend allow-list (admin-library.ts) for immediate UX feedback;
// the backend stays the enforcer.
const ALLOWED_VARS: Record<string, string[]> = {
  audit_followup: ["first_name", "company_name"],
  audit_nudge: ["first_name", "company_name"],
  cold_outreach: ["first_name", "company_name"],
  transactional: ["company_name", "report", "cta"],
};
const DEFAULT_ALLOWED = ["first_name", "company_name"];
// Injected from the row's cta_label/cta_url columns by the render path (#135).
const ROW_SOURCED = ["cta_label", "cta_url"];

function deriveVars(...texts: Array<string | null | undefined>): string[] {
  const found = new Set<string>();
  for (const t of texts) {
    if (!t) continue;
    for (const m of t.matchAll(TOKEN_RE)) found.add(m[1]);
  }
  return [...found];
}

/** Make the always-on live pane readable: substitute tokens with sample values
 * + the actual CTA, instead of showing raw {{…}}. (The "Preview" button renders
 * the full assembled email server-side for the faithful view.) */
function previewBody(md: string, ctaLabel: string, ctaUrl: string): string {
  return md
    .replace(/\{\{\s*first_name\s*\}\}/g, "Marie")
    .replace(/\{\{\s*company_name\s*\}\}/g, "Atelier Dupont")
    .replace(/\{\{\s*report\s*\}\}/g, "\n\n> 📊 *[ le rapport d'audit s'insère ici ]*\n\n")
    .replace(/\{\{\s*cta\s*\}\}/g, () => `\n\n👉 **[${ctaLabel || "Planifier 15 min"}](${ctaUrl || "#"})**\n\n`)
    .replace(/\{\{\s*cta_url\s*\}\}/g, () => ctaUrl || "#")
    .replace(/\{\{\s*cta_label\s*\}\}/g, () => ctaLabel || "CTA")
    .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, "[$1]");
}

/** Surface a useful reason for both error shapes: TemplateError → {message},
 * Zod failures → {issues:[{message}]}. */
function errMsg(json: Record<string, unknown>, status: number): string {
  if (typeof json.message === "string") return json.message;
  if (Array.isArray(json.issues)) {
    const msg = json.issues
      .map((i) => (i as { message?: string }).message ?? "")
      .filter(Boolean)
      .join("; ");
    if (msg) return msg;
  }
  return `HTTP ${status}`;
}

export function Templates() {
  const [token, setToken] = useState<string>(() =>
    typeof window !== "undefined" ? window.sessionStorage.getItem(SK_TOKEN) ?? "" : "",
  );
  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Editor fields.
  const [subject, setSubject] = useState<string>("");
  const [bodyMd, setBodyMd] = useState<string>("");
  const [channel, setChannel] = useState<Channel>("email");
  const [category, setCategory] = useState<string>("");
  const [active, setActive] = useState<boolean>(true);
  const [ctaLabel, setCtaLabel] = useState<string>("");
  const [ctaUrl, setCtaUrl] = useState<string>("");

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Test-send (#137).
  const [testTo, setTestTo] = useState("contact@levier-ia.fr");
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState<string | null>(null);

  // Create form (#136).
  const [showCreate, setShowCreate] = useState(false);
  const [newId, setNewId] = useState("");
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("audit_followup");
  const [newChannel, setNewChannel] = useState<Channel>("email");
  const [newBody, setNewBody] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!token) return;
      setLoading(true);
      setListError(null);
      fetch("/api/_admin/templates", { headers: headers(), signal })
        .then(async (res) => {
          if (res.status === 401) throw new Error("Token opérateur invalide (401).");
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<{ templates: Template[] }>;
        })
        .then(({ templates: t }) => {
          setTemplates(t);
          setLoading(false);
        })
        .catch((err: Error) => {
          if (err.name === "AbortError") return;
          setListError(err.message);
          setLoading(false);
        });
    },
    [token, headers],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const select = (t: Template) => {
    setSelectedId(t.id);
    setSubject(t.subject ?? "");
    setBodyMd(t.body_md);
    setChannel((CHANNELS as readonly string[]).includes(t.channel) ? (t.channel as Channel) : "email");
    setCategory(t.category);
    setActive(t.active);
    setCtaLabel(t.cta_label ?? "");
    setCtaUrl(t.cta_url ?? "");
    setSavedAt(null);
    setTestStatus(null);
    setEditError(null);
    setPreview(null);
  };

  const derivedVars = useMemo(() => deriveVars(subject, bodyMd), [subject, bodyMd]);
  const allowed = [...(ALLOWED_VARS[category] ?? DEFAULT_ALLOWED), ...ROW_SOURCED];

  const grouped = useMemo(() => {
    const m = new Map<string, Template[]>();
    for (const t of templates) {
      const arr = m.get(t.category) ?? [];
      arr.push(t);
      m.set(t.category, arr);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [templates]);

  const saveToken = (t: string) => {
    setToken(t);
    if (typeof window !== "undefined") window.sessionStorage.setItem(SK_TOKEN, t);
  };

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    setEditError(null);
    setSavedAt(null);
    try {
      const res = await fetch(`/api/_admin/templates/${encodeURIComponent(selectedId)}`, {
        method: "PUT",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: subject === "" ? null : subject,
          body_md: bodyMd,
          channel,
          active,
          cta_label: ctaLabel === "" ? null : ctaLabel,
          cta_url: ctaUrl === "" ? null : ctaUrl,
        }),
      });
      const json = (await res.json()) as Record<string, unknown>;
      if (!res.ok) throw new Error(errMsg(json, res.status));
      const updated = json as unknown as Template;
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setSavedAt(Date.now());
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const createTpl = async () => {
    setCreateBusy(true);
    setCreateError(null);
    try {
      const res = await fetch("/api/_admin/templates", {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          id: newId.trim(),
          name: newName.trim(),
          category: newCategory.trim(),
          channel: newChannel,
          body_md: newBody,
        }),
      });
      const json = (await res.json()) as Record<string, unknown>;
      if (!res.ok) throw new Error(errMsg(json, res.status));
      const created = json as unknown as Template;
      setTemplates((prev) => [created, ...prev]);
      setShowCreate(false);
      setNewId("");
      setNewName("");
      setNewBody("");
      select(created);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreateBusy(false);
    }
  };

  const hardDelete = async () => {
    if (!selectedId) return;
    if (!confirm(`Supprimer définitivement « ${selectedId} » ? (refusé si une séquence active le référence)`)) return;
    setDeleting(true);
    setEditError(null);
    try {
      const res = await fetch(`/api/_admin/templates/${encodeURIComponent(selectedId)}?hard=true`, {
        method: "DELETE",
        headers: headers(),
      });
      const json = (await res.json()) as Record<string, unknown>;
      if (!res.ok) throw new Error(errMsg(json, res.status));
      setTemplates((prev) => prev.filter((t) => t.id !== selectedId));
      setSelectedId(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const testSend = async () => {
    setTesting(true);
    setTestStatus(null);
    setEditError(null);
    try {
      const res = await fetch("/api/_admin/templates/test-send", {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: subject === "" ? null : subject,
          body_md: bodyMd,
          category,
          channel,
          cta_label: ctaLabel === "" ? null : ctaLabel,
          cta_url: ctaUrl === "" ? null : ctaUrl,
          to: testTo,
        }),
      });
      const json = (await res.json()) as { status?: string; verdict?: string; feedback?: string[]; error?: string };
      if (!res.ok) throw new Error(errMsg(json as Record<string, unknown>, res.status));
      if (json.status === "sent") setTestStatus(`✓ Envoyé à ${testTo}`);
      else if (json.status === "blocked") setTestStatus(`⛔ Bloqué par le gate (${json.verdict ?? ""}) : ${(json.feedback ?? []).join(" ; ")}`);
      else if (json.status === "ceiling") setTestStatus("⛔ Plafond d'envoi quotidien atteint.");
      else if (json.status === "unavailable") setTestStatus("Binding EMAIL non configuré (dev/local).");
      else setTestStatus(`Erreur : ${json.error ?? "inconnue"}`);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    setEditError(null);
    try {
      const res = await fetch("/api/_admin/templates/preview", {
        method: "POST",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: subject === "" ? null : subject,
          body_md: bodyMd,
          category,
          channel,
          cta_label: ctaLabel === "" ? null : ctaLabel,
          cta_url: ctaUrl === "" ? null : ctaUrl,
        }),
      });
      const json = (await res.json()) as Record<string, unknown>;
      if (!res.ok) throw new Error(errMsg(json, res.status));
      setPreview(json as unknown as PreviewResult);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewing(false);
    }
  };

  // ── Token gate ─────────────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="space-y-6">
        <header className="reveal">
          <p className="eyebrow">Console · 15</p>
          <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
            <span className="text-[var(--color-text)]">Templates </span>
            <span className="text-accent-gradient">email</span>
          </h2>
        </header>
        <div className="panel p-5 sm:p-6 max-w-md w-full">
          <p className="eyebrow mb-3">KB_SEED_TOKEN requis</p>
          <p className="text-sm text-[var(--color-text-muted)] mb-3">
            L'édition des templates est gated par le secret KB_SEED_TOKEN (le même que la page Admin). Collez-le ici (stocké en sessionStorage, supprimé à la fermeture de l'onglet).
          </p>
          <input
            type="password"
            placeholder="kb_seed_token..."
            onKeyDown={(e) => {
              if (e.key === "Enter") saveToken((e.target as HTMLInputElement).value);
            }}
            className="w-full h-12 px-3 rounded font-mono text-base sm:text-sm"
            style={inputStyle}
          />
          <p className="font-mono text-[10px] text-[var(--color-text-dim)] mt-2">Press Enter to save</p>
        </div>
      </div>
    );
  }

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  return (
    <div className="space-y-6 lg:space-y-8">
      <header className="reveal flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">Console · 15</p>
          <h2 className="font-display italic text-3xl sm:text-4xl lg:text-[56px] leading-[0.95] mt-2 tracking-tight">
            <span className="text-[var(--color-text)]">Templates </span>
            <span className="text-accent-gradient">email</span>
          </h2>
          <p className="mt-3 text-sm text-[var(--color-text-muted)] max-w-2xl">
            Éditez le texte et le CTA des relances/nudges, sans déploiement. Le gate anti-hype, le pixel d'ouverture et la désinscription restent appliqués à l'envoi.
          </p>
        </div>
        <button
          onClick={() => saveToken("")}
          className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-dim)] hover:text-[var(--color-accent)]"
        >
          Forget token
        </button>
      </header>

      {listError && (
        <div className="panel p-3" style={{ borderColor: "var(--color-status-error)", background: "rgba(239,68,68,0.04)" }}>
          <p className="font-mono text-xs text-[var(--color-status-error)]">{listError}</p>
        </div>
      )}
      {loading && templates.length === 0 && (
        <p className="font-mono text-xs text-[var(--color-text-dim)] blink">Loading…</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* ── Master list ─────────────────────────────────────────────────── */}
        <div className="space-y-4">
          <button
            onClick={() => setShowCreate((s) => !s)}
            className="w-full inline-flex items-center justify-center gap-2 font-mono text-xs px-3 py-2 rounded transition-colors"
            style={{ background: "var(--color-accent-soft)", color: "var(--color-accent)", border: "1px solid var(--color-accent)" }}
          >
            <Plus className="size-3.5" strokeWidth={2} /> Nouveau template
          </button>
          {showCreate && (
            <div className="panel p-3 space-y-2">
              <input
                value={newId}
                onChange={(e) => setNewId(e.target.value)}
                placeholder="id (ex: audit_followup_step4)"
                className="w-full font-mono text-xs px-2 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                style={inputStyle}
              />
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="nom lisible"
                className="w-full font-mono text-xs px-2 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                style={inputStyle}
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="catégorie"
                  className="font-mono text-xs px-2 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                  style={inputStyle}
                />
                <select
                  value={newChannel}
                  onChange={(e) => setNewChannel(e.target.value as Channel)}
                  className="font-mono text-xs px-2 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                  style={inputStyle}
                >
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <textarea
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                rows={4}
                placeholder="Corps (markdown). Email cold-outbound : garder une ligne « Répondez STOP »."
                className="w-full font-mono text-xs px-2 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)] resize-y"
                style={inputStyle}
              />
              {createError && <p className="font-mono text-[10px] text-[var(--color-status-error)]">{createError}</p>}
              <button
                onClick={createTpl}
                disabled={createBusy || !newId.trim() || !newName.trim() || !newBody.trim()}
                className="w-full font-mono text-xs px-3 py-1.5 rounded disabled:opacity-40 transition-opacity"
                style={{ background: "var(--color-accent)", color: "white" }}
              >
                {createBusy ? "Création…" : "Créer"}
              </button>
            </div>
          )}
          {grouped.map(([cat, items]) => (
            <div key={cat}>
              <p className="eyebrow mb-2">{cat}</p>
              <div className="space-y-1.5">
                {items.map((t) => {
                  const isSel = t.id === selectedId;
                  return (
                    <button
                      key={t.id}
                      onClick={() => select(t)}
                      className="w-full text-left px-3 py-2 rounded transition-colors"
                      style={{
                        background: isSel ? "var(--color-accent-soft)" : "var(--color-bg-card)",
                        border: `1px solid ${isSel ? "var(--color-accent)" : "var(--color-border)"}`,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-[var(--color-text)] flex-1 truncate">{t.id}</span>
                        <span className="pill" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)" }}>
                          {t.channel}
                        </span>
                        <span
                          className="size-1.5 rounded-full shrink-0"
                          style={{ background: t.active ? "var(--color-status-active)" : "var(--color-text-dim)" }}
                          title={t.active ? "actif" : "inactif"}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* ── Detail editor ───────────────────────────────────────────────── */}
        {!selected ? (
          <div className="panel p-6 grid place-items-center text-center">
            <div>
              <Mail className="size-6 text-[var(--color-text-dim)] mx-auto" strokeWidth={1.5} />
              <p className="mt-3 font-mono text-xs text-[var(--color-text-dim)]">
                Sélectionnez un template à gauche pour l'éditer.
              </p>
            </div>
          </div>
        ) : (
          <section className="panel p-5 sm:p-6 space-y-5">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-[var(--color-accent-bright)]">{selected.id}</span>
              <span className="pill" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)" }}>{category}</span>
              <label className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] cursor-pointer">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                Actif
              </label>
            </div>

            {category === "transactional" && (
              <div
                className="panel p-3"
                style={{ borderColor: "var(--color-accent)", background: "var(--color-accent-soft)" }}
              >
                <p className="font-mono text-[11px] text-[var(--color-text-muted)]">
                  📧 Email d'audit transactionnel. Utilisez <code>{"{{report}}"}</code> et <code>{"{{cta}}"}</code> pour placer le rapport et le bouton ; le pixel d'ouverture, le tracking de clic et la désinscription RGPD sont appliqués <strong>côté serveur</strong> (non éditables ici). Le message d'accroche (chaud/froid) reste géré par le code.
                </p>
              </div>
            )}

            <label className="block space-y-1.5">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">
                Sujet {channel !== "email" && "(optionnel pour ce canal)"}
              </span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={channel === "email" ? "Objet de l'email" : "(aucun)"}
                className="w-full font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                style={inputStyle}
              />
            </label>

            <label className="block space-y-1.5">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">Canal</span>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as Channel)}
                className="w-full font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                style={inputStyle}
              >
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            {/* Structured CTA — single source for the booking URL (#135). Use
                {{cta_url}} / {{cta_label}} in the body to reference these. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block space-y-1.5">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">CTA — libellé</span>
                <input
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  placeholder="Planifier 15 min"
                  className="w-full font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                  style={inputStyle}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">CTA — URL</span>
                <input
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://cal.com/…"
                  className="w-full font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                  style={inputStyle}
                />
              </label>
            </div>
            <p className="font-mono text-[10px] text-[var(--color-text-dim)] -mt-2">
              Référencez-les dans le corps avec <code>{"{{cta_label}}"}</code> / <code>{"{{cta_url}}"}</code> pour une source unique de l'URL.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block space-y-1.5">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">Corps (markdown)</span>
                <textarea
                  value={bodyMd}
                  onChange={(e) => setBodyMd(e.target.value)}
                  rows={16}
                  className="w-full font-mono text-xs px-3 py-2 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)] resize-y"
                  style={inputStyle}
                />
              </label>
              <div className="space-y-1.5">
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">Aperçu (corps, valeurs d'exemple)</span>
                <div className="panel p-4 overflow-auto" style={{ maxHeight: "26rem", background: "var(--color-bg-card)" }}>
                  {renderMarkdown(previewBody(bodyMd, ctaLabel, ctaUrl) || "_(vide)_")}
                </div>
              </div>
            </div>

            {/* Variable chips — auto-derived, red when the send path can't supply them. */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">Variables :</span>
              {derivedVars.length === 0 && <span className="font-mono text-[10px] text-[var(--color-text-dim)]">aucune</span>}
              {derivedVars.map((v) => {
                const ok = allowed.includes(v);
                return (
                  <span
                    key={v}
                    className="pill font-mono text-[10px]"
                    style={{
                      color: ok ? "var(--color-text-muted)" : "var(--color-status-error)",
                      borderColor: ok ? "var(--color-border)" : "var(--color-status-error)",
                    }}
                    title={ok ? "" : `Le send path ne fournit pas {{${v}}} (autorisés : ${allowed.join(", ")})`}
                  >
                    {`{{${v}}}`}{!ok && " ⚠"}
                  </span>
                );
              })}
            </div>

            {editError && <p className="font-mono text-[11px] text-[var(--color-status-error)]">{editError}</p>}

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 font-mono text-xs px-4 py-2 rounded disabled:opacity-40 transition-opacity"
                style={{ background: "var(--color-accent)", color: "white" }}
              >
                <Save className="size-3.5" strokeWidth={2} />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button
                onClick={runPreview}
                disabled={previewing}
                className="inline-flex items-center gap-2 font-mono text-xs px-4 py-2 rounded disabled:opacity-40 transition-opacity"
                style={{ background: "var(--color-accent-soft)", color: "var(--color-accent)", border: "1px solid var(--color-accent)" }}
              >
                <Eye className="size-3.5" strokeWidth={2} />
                {previewing ? "Rendu…" : "Preview & lint"}
              </button>
              <button
                onClick={hardDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 font-mono text-xs px-4 py-2 rounded disabled:opacity-40 transition-opacity ml-auto"
                style={{ color: "var(--color-status-error)", border: "1px solid rgba(239,68,68,0.4)" }}
                title="Suppression définitive — refusée si une séquence active référence ce template (sinon, désactivez via le toggle Actif)."
              >
                <Trash2 className="size-3.5" strokeWidth={2} />
                {deleting ? "Suppression…" : "Supprimer"}
              </button>
              {savedAt && <span className="font-mono text-[10px] text-[var(--color-status-active)]">✓ Enregistré</span>}
            </div>

            {/* Real test-send (#137) — full gate + ceiling slot, to a @levier-ia.fr address. */}
            <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[var(--color-border)]">
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-text-dim)]">Test :</span>
              <input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="moi@levier-ia.fr"
                className="font-mono text-xs px-2 py-1.5 rounded outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                style={{ ...inputStyle, minWidth: "220px" }}
              />
              <button
                onClick={testSend}
                disabled={testing}
                className="font-mono text-xs px-3 py-1.5 rounded disabled:opacity-40 transition-opacity"
                style={{ background: "var(--color-bg-card)", color: "var(--color-text-muted)", border: "1px solid var(--color-border)" }}
                title="Envoie un vrai email de test (passe le gate anti-hype + consomme un slot du plafond quotidien)."
              >
                {testing ? "Envoi…" : "Envoyer un test"}
              </button>
              {testStatus && (
                <span
                  className="font-mono text-[10px]"
                  style={{ color: testStatus.startsWith("✓") ? "var(--color-status-active)" : "var(--color-status-error)" }}
                >
                  {testStatus}
                </span>
              )}
            </div>

            {preview && (
              <div className="space-y-2 pt-2 border-t border-[var(--color-border)]">
                <p className="eyebrow">{preview.full ? "Email complet (rendu serveur, société + rapport d'exemple, non envoyé)" : "Rendu serveur (non envoyé)"}</p>
                {preview.subject && <p className="font-mono text-xs text-[var(--color-text-muted)]">Sujet : {preview.subject}</p>}
                {preview.full ? (
                  <iframe
                    srcDoc={preview.html}
                    title="Aperçu de l'email complet"
                    className="w-full rounded"
                    style={{ height: "520px", border: "1px solid var(--color-border)", background: "#ffffff" }}
                  />
                ) : (
                  <div className="panel p-4" style={{ background: "var(--color-bg-card)" }}>{renderMarkdown(preview.text)}</div>
                )}
                <div
                  className="panel p-3"
                  style={{
                    borderColor: preview.lint.verdict === "PASS" ? "var(--color-border)" : "var(--color-status-error)",
                    background: preview.lint.verdict === "PASS" ? "var(--color-bg-card)" : "rgba(239,68,68,0.04)",
                  }}
                >
                  <p className="font-mono text-[11px]">
                    Lint (conseil) :{" "}
                    <span style={{ color: preview.lint.verdict === "PASS" ? "var(--color-status-active)" : "var(--color-status-error)" }}>
                      {preview.lint.verdict}
                    </span>
                  </p>
                  {preview.lint.feedback.length > 0 && (
                    <ul className="mt-1.5 space-y-1">
                      {preview.lint.feedback.map((f, i) => (
                        <li key={i} className="font-mono text-[11px] text-[var(--color-text-muted)]">• {f}</li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-2 font-mono text-[10px] text-[var(--color-text-dim)]">
                    Le gate réel s'applique à l'envoi — ce lint est indicatif.
                  </p>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
